import React from "react";
import { ArrowUp, ArrowDown, Minus } from "../../icons";
/* KPITile + KPIStrip — Phase B primitive (expanded in Phase 4 sub-step 2,
   Phase 2d types, Phase 3d direction icons).

   KPIStrip: container, CSS grid auto-fitting tiles. cols={1|2|3|4} or "auto".

   KPITile: one KPI card. Slots designed to cover every legacy variant the
   Italy dashboard's 4-tile strip needs (NOW / PATH / HEALTH / ECONOMICS),
   plus the legacy `label` shortcut for simpler showcase usage.

   ── Head section ──────────────────────────────────────────────────────
     title        ReactNode — primary head label (typically with InfoIcon).
                  Prefer `title` over `label` when the head needs a tooltip
                  or composed content. `label` is the simpler scalar form;
                  if both are set, `title` wins.
     label        string|ReactNode — simple text head (legacy/showcase).
     pill         ReactNode — right-aligned status pill (e.g. on-track dot).
     control      ReactNode — right-aligned interactive control (e.g. a
                  multi-select). Mutually exclusive with `pill` and `sub`.
     sub          ReactNode — small right-aligned subtitle (e.g. "MoM (Apr)").

   ── Hero section ──────────────────────────────────────────────────────
     value        ReactNode — large hero figure.
     valueLabel   ReactNode — caption below the hero figure.
     valueSub     ReactNode — second caption line (e.g. upsell breakdown).
     delta        { value: string, tone: "good"|"warn"|"bad"|"neutral" }
                  Compact change indicator. Shown below value, separate row.

   ── Body / children ───────────────────────────────────────────────────
     children     arbitrary content rendered between hero and rows
                  (used by the HEALTH tile for channel breakdowns).

   ── Bottom rows ───────────────────────────────────────────────────────
     rows         [{ label, value, tone? }] — small key:value rows.

   ── Modifiers ─────────────────────────────────────────────────────────
     accent       structural slot: "now" | "path" | "health" | "econ"
                  (was a free-form string with status mixed in pre-2d).
                  String fallback still accepted for back-compat with
                  legacy callers that pass space-separated modifiers like
                  "now on-track".
     status       Phase 2d Rich — overlay status: "on-track" | "ahead"
                  | "behind" | "warn". Layered as v2-kpi-tile--<status>.
                  Use this for tone signals on top of the structural slot
                  rather than packing them into `accent`.
     sticky       bool — applies sticky positioning (IA.md D7).
     className    extra classes.
*/

export type KPITileAccent = "now" | "path" | "health" | "econ" | string;
export type KPITileStatus = "on-track" | "ahead" | "behind" | "warn";

export function KPIStrip({ cols = 4, className = "", children, ...rest }: any) {
  const colClass = typeof cols === "number" ? `v2-kpi-strip--cols-${cols}` : "v2-kpi-strip--cols-auto";
  return (
    <div className={`v2-kpi-strip ${colClass} ${className}`.trim()} role="list" {...rest}>
      {children}
    </div>
  );
}

export default function KPITile({
  // Head
  title,
  label,
  pill,
  control,
  sub,
  // Hero
  value,
  valueLabel,
  valueSub,
  delta,
  // Body
  children,
  // Rows
  rows,
  // Modifiers
  accent,
  status,
  sticky = false,
  className = "",
  ...rest
}: any) {
  // Accent can be multiple space-separated tokens (e.g. "now kpi-tile--on-track").
  // Map each to v2-kpi-tile--<token>. If a token already starts with v2- or kpi-tile--
  // leave it alone — caller knows the class they want.
  // Phase 2d: status is a typed separate prop; merged into the same class
  // list so existing CSS rules keep working.
  const accentClasses = accent
    ? String(accent).split(/\s+/).filter(Boolean).map(t =>
        t.startsWith("v2-") || t.startsWith("kpi-tile--") ? t : `v2-kpi-tile--${t}`
      )
    : [];
  const statusClass = status ? `v2-kpi-tile--${status}` : "";

  const cls = [
    "v2-kpi-tile",
    ...accentClasses,
    statusClass,
    sticky ? "v2-kpi-tile--sticky" : "",
    className,
  ].filter(Boolean).join(" ");

  // Head right side: pill > control > sub (priority order, only one shown).
  const headRight = pill ?? control ?? (sub != null ? <span className="v2-kpi-tile__sub">{sub}</span> : null);
  const headTitle = title != null
    ? <span className="v2-kpi-tile__title">{title}</span>
    : (label != null ? <span className="v2-kpi-tile__label">{label}</span> : null);
  const showHead = headTitle || headRight;

  const showHero = value != null || valueLabel != null || valueSub != null;

  return (
    <article className={cls} role="listitem" {...rest}>
      {showHead ? (
        <div className="v2-kpi-tile__head">
          {headTitle}
          {headRight}
        </div>
      ) : null}

      {showHero ? (
        <div className="v2-kpi-tile__hero">
          {value != null ? (
            <div className="v2-kpi-tile__value tabular">{value}</div>
          ) : null}
          {valueLabel != null ? (
            <div className="v2-kpi-tile__value-label">{valueLabel}</div>
          ) : null}
          {valueSub != null ? (
            <div className="v2-kpi-tile__value-sub">{valueSub}</div>
          ) : null}
        </div>
      ) : null}

      {delta ? (
        /* Phase 3d — direction icon pairs with delta color so the signal
           isn't color-only. Direction is inferred from the value's leading
           character: '+' or '↑' → up; '-' / '−' / '↓' → down; else flat.
           If the caller wants explicit control they can prepend '+'/'-' to
           the value string. */
        <div className={`v2-kpi-tile__delta v2-kpi-tile__delta--${delta.tone || "neutral"}`}>
          {(() => {
            const v = String(delta.value || "").trim();
            const first = v.charAt(0);
            if (first === "+" || first === "↑") return <ArrowUp   size={12} />;
            if (first === "-" || first === "−" || first === "↓") return <ArrowDown size={12} />;
            return <Minus size={12} />;
          })()}
          <span style={{ marginLeft: 2 }}>{delta.value}</span>
        </div>
      ) : null}

      {children}

      {Array.isArray(rows) && rows.length > 0 ? (
        <div className="v2-kpi-tile__rows">
          {rows.map((r: any, i: number) => r == null ? null : (
            <div key={i} className="v2-kpi-tile__row">
              <span className="v2-kpi-tile__row-label">{r.label}</span>
              <span className={`v2-kpi-tile__row-value tabular ${r.tone ? `v2-kpi-tile__row-value--${r.tone}` : ""}`.trim()}>
                {r.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}
